import {
  Stack,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from 'expo-router';
import Head from 'expo-router/head';
import { useEffect, useState } from 'react';
import { YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSInput from '@/components/ui/PSInput';
import PSText from '@/components/ui/PSText';
import { PSErrorClass } from '@/src/api/apiError';
import authApi from '@/src/api/authApi';
import { useAuthContext } from '@/src/lib/authContext';
import { isValidEmail } from '@/src/utils/validation';
import { toast } from '@tamagui/toast/v2';
import { setRestoredUser, useSessionUser } from '@/src/lib/sessionStore';

/**
 * Auth step — email. NOT part of a fresh signup (see `details.tsx`'s
 * docstring): this screen only exists as a booking-time detour, reached
 * directly (bypassing mobile/verify-mobile/details entirely) once a session
 * already exists in state 2 (has a name, no email) or state 3 (has an email,
 * unverified). Both states land here rather than branching — the email input
 * prefills from whatever's already on the user, and submitting always
 * re-requests a fresh OTP, so there's nothing state-specific to draw.
 *
 * NO headerLeft and no swipe-dismiss (gesture off in the layout, matching
 * `details.tsx`): by the time a session can reach this screen the mobile (and
 * usually the name) is already verified, so it's forward-only same as there.
 *
 * Submitting requests an email-verification code. `emailChallengeId` present
 * is the normal case — it means a code went out and the OTP screen is owed.
 * Its absence is a defensive fallback (the address was already this user's
 * verified one — shouldn't happen given the states that route here, but
 * `details.tsx`'s old combined screen had the same fallback and it costs
 * nothing to keep).
 */
export default function AuthEmail() {
  const router = useRouter();
  const rootNavigation = useNavigation('/');
  const { email, setEmail, markSent } = useAuthContext();
  const [emailError, setEmailError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const user = useSessionUser();
  const { headerCopy } = useLocalSearchParams<{ headerCopy?: string }>();

  const emailValid = isValidEmail(email);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user, setEmail]);

  const handleSubmit = async () => {
    if (!emailValid) {
      setEmailError('Enter a valid email address');
      return;
    }

    // The draft value is what verify-email prints back and what the request
    // carries, so the stored address has to be exactly the one we approved —
    // `isValidEmail` forgives a trailing space, an SMTP server won't.
    const trimmedEmail = email.trim();
    setEmail(trimmedEmail);

    setSubmitting(true);
    try {
      const { user: updatedUser, emailChallengeId } = await authApi.updateMe({
        email: trimmedEmail,
      });
      setRestoredUser(updatedUser);

      if (emailChallengeId) {
        markSent('email', trimmedEmail, emailChallengeId);
        router.push('/auth/verify-email');
      } else {
        closeAuthModal();
      }
    } catch (err) {
      // A 409 (belongs to another account) or 400 (malformed) are verdicts on
      // the email — the field is right here, so they go under it.
      if (
        err instanceof PSErrorClass &&
        (err.field === 'email' || err.code === 'conflict')
      ) {
        setEmailError(err.message);
      } else {
        toast.error(
          err instanceof PSErrorClass
            ? err.message
            : 'Something went wrong. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  /** See the note in verify-mobile: leaving the flow is a ROOT-stack pop. */
  const closeAuthModal = () => {
    if (rootNavigation.canGoBack()) {
      rootNavigation.goBack();
    } else {
      router.replace('/');
    }
  };

  return (
    <>
      <Head>
        <title>Your email · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerBackVisible: false,
          headerShown: true,
        }}
      />

      <YStack px="$lg" gap="$lg" pt={'$space.lg'}>
        <YStack gap="$xs">
          <PSText.H1 color="$textDark">
            {headerCopy ? headerCopy : "What's your email?"}
          </PSText.H1>
          <PSText.Body3 color="$textDarkMuted">
            We&apos;ll send your tickets and invoices here, so make sure
            it&apos;s an address you check.
          </PSText.Body3>
        </YStack>
        <YStack gap="$xs">
          <PSText.Body3 color="$textDarkMuted">Email</PSText.Body3>
          <PSInput
            type="email"
            value={email}
            onChangeText={setEmail}
            autoFocus
            error={emailError}
            onFocus={() => setEmailError(undefined)}
            onBlur={() => {
              setEmail(email.trim());
              setEmailError(
                emailValid ? undefined : 'Enter a valid email address'
              );
            }}
          />
        </YStack>
        <PSButton
          title="Submit"
          loading={submitting}
          loadingTitle="Saving"
          onPress={handleSubmit}
          disabled={!emailValid}
        />
      </YStack>
    </>
  );
}
